import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useWebSocketCall } from '../../../hooks/use-websocket-call';
import { cn } from '../../../utils';
import type { QCallFloatingConfig, ConnectionState } from '../../../types';
import { StyleContainer } from '../shared';
import { MinimalFloatingButton } from './minimal-floating-button';
import { MinimalWidgetPanel } from './minimal-widget-panel';
import { MicIcon, MicOffIcon, LoaderIcon, BotIcon } from '../../shared/icons';

export interface MinimalStyleContainerProps {
  /** Widget configuration */
  config: QCallFloatingConfig;
  /** Additional class names */
  className?: string;
}

/**
 * Minimal style container - Minimal-style widget with inline transcript
 */
export function MinimalStyleContainer({ config, className }: MinimalStyleContainerProps) {
  const [isOpen, setIsOpen] = useState(config.autoOpen ?? false);
  const [callDuration, setCallDuration] = useState(0);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const {
    connect,
    disconnect,
    connectionState,
    isAudioMuted,
    toggleAudio,
    error,
    transcript,
  } = useWebSocketCall({
    wsUrl: config.wsUrl,
    assistantId: config.assistantId || '',
    userId: config.userId,
    workspaceId: config.workspaceId,
    apiBaseUrl: config.apiBaseUrl,
    language: config.language,
    voiceModal: config.voiceModal,
    voiceName: config.voiceName,
    onConnect: config.onConnect,
    onDisconnect: config.onDisconnect,
    onError: config.onError,
  });

  const {
    position = 'bottom-left',
    offset,
    avatar,
    agentName = 'AI Assistant',
    onConnect,
    onDisconnect,
    onError,
  } = config;

  // Timer effect
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (connectionState === 'connected') {
      interval = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    } else {
      setCallDuration(0);
    }
    return () => clearInterval(interval);
  }, [connectionState]);

  // Auto-scroll transcript to bottom
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [transcript]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleToggle = useCallback(() => {
    if (!isOpen) {
      handleStartCall();
    }
    setIsOpen((prev) => !prev);
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleToggleMute = useCallback(() => {
    toggleAudio();
  }, [toggleAudio]);

  const handleEndCall = useCallback(() => {
    disconnect();
    setCallDuration(0);
    setIsOpen(false);
  }, [disconnect]);

  const handleStartCall = useCallback(async () => {
    await connect();
  }, [connect]);

  // If floating button is clicked, open panel and start call
  const handleFloatingClick = useCallback(() => {
    setIsOpen(true);
    if (connectionState === 'idle') {
      handleStartCall();
    }
  }, [connectionState, handleStartCall]);

  const isConnected = connectionState === 'connected';
  const isConnecting = connectionState === 'connecting';

  return (
    <StyleContainer uiStyle="minimal" className={className}>
      <MinimalFloatingButton
        onClick={handleFloatingClick}
        position={position}
        avatar={avatar}
        agentName={agentName}
        status={connectionState}
        offset={offset}
        isHidden={isOpen}
      />

      <MinimalWidgetPanel
        isOpen={isOpen}
        onClose={handleClose}
        position={position}
      >
        <div className="flex flex-col items-center flex-1 w-full h-full relative">

          {/* Avatar + Timer (compact) */}
          <div className="flex-shrink-0 flex flex-col items-center w-full mt-3">
            <div className="relative">
              <div
                className={cn(
                  "w-[52px] h-[52px] rounded-full flex items-center justify-center transition-transform duration-700 overflow-hidden",
                  isConnecting && "animate-pulse scale-95",
                  isConnected && "scale-105"
                )}
              >
                {avatar ? (
                  <img src={avatar} alt={agentName} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                    <BotIcon className="w-6 h-6 text-white" />
                  </div>
                )}
              </div>
            </div>

            <div className="mt-2 text-white font-mono text-[11px] font-medium tracking-wide">
              {isConnected ? formatTime(callDuration) : '00:00'}
            </div>
            {isConnecting && (
              <div className="mt-0.5 text-white/50 text-[10px]">Connecting...</div>
            )}
            {connectionState === 'error' && (
              <div className="mt-0.5 text-red-400 text-[10px]">Connection Failed</div>
            )}
          </div>

          {/* Inline Transcript - expands to fill available space */}
          <div className="flex-1 min-h-0 w-full px-3 mt-2">
            {transcript.length > 0 && (
              <div
                ref={chatContainerRef}
                className="h-full overflow-y-auto rounded-lg bg-white/5 p-2 qcall-scrollbar"
              >
                {transcript.map((msg) => (
                  <div
                    key={msg.id}
                    className={cn(
                      "mb-1 last:mb-0 flex",
                      msg.speaker === 'user' ? "justify-end" : "justify-start"
                    )}
                  >
                    <div
                      className={cn(
                        "max-w-[80%] rounded-lg px-2 py-1 text-[11px] leading-snug",
                        msg.speaker === 'user'
                          ? "bg-white/15 text-white"
                          : "bg-white/10 text-white/80"
                      )}
                    >
                      {msg.text}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Bottom Controls */}
          <div className="flex-shrink-0 flex items-center justify-center gap-3 mt-auto mb-2 w-full px-5 pt-2">
            {/* Mic Toggle */}
            <button
              className={cn(
                "flex items-center justify-center w-9 h-9 rounded-full transition-colors shadow-sm",
                isAudioMuted ? "bg-red-500 hover:bg-red-600 text-white" : "bg-[#165a50] hover:bg-[#124d44] text-white"
              )}
              onClick={handleToggleMute}
              disabled={!isConnected}
              aria-label={isAudioMuted ? "Unmute mic" : "Mute mic"}
            >
              {isAudioMuted ? <MicOffIcon className="w-4 h-4" /> : <MicIcon className="w-4 h-4" />}
            </button>

            {/* End Call Pill Button */}
            <button
              className="flex items-center justify-center gap-2 h-9 px-6 rounded-full bg-[#ef4444] hover:bg-[#dc2626] text-white transition-colors shadow-sm text-[12px] font-medium"
              onClick={handleEndCall}
              disabled={!isConnected && !isConnecting}
            >
              {isConnecting ? (
                <LoaderIcon className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" className="text-white">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  </svg>
                  <span>End</span>
                </>
              )}
            </button>
          </div>

          {/* Error message */}
          {error && (
            <div
              className="flex-shrink-0 mx-3 mb-2 p-2 rounded-lg text-left"
              style={{
                backgroundColor: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
              }}
            >
              <div className="flex items-start gap-1.5">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fca5a5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <p style={{ fontSize: '10px', color: '#fca5a5', lineHeight: 1.5 }}>
                  {error.message}
                </p>
              </div>
            </div>
          )}
        </div>
      </MinimalWidgetPanel>
    </StyleContainer>
  );
}
